import React, {
  useCallback, useState, useMemo, useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { InputNumber, Row, Select, Divider } from 'antd';
import { DateTime } from 'luxon';

import CheckRows from '../CheckRows';
import CompanySettingsCard from '../CompanySettingsCard';
import UserAssignmentSelector from '../../forms/FormWorkflows/selectors/UserAssignmentSelector';
import CompanyEditRow from '../CompanyEditRow';
import Debouncer from '../../helpers/Debouncer';
import { AdditionalItem, AdditionalLabel } from '../../forms/FormWorkflows/AssignmentDropdownItems';
import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import OnTraccrSwitch from '../../common/inputs/OnTraccrSwitch';
import OnTraccrTimePicker from '../../common/inputs/OnTraccrTimePicker';
import NotificationRecipientGroup from './NotificationRecipientGroup';
import UnsubscribedEmailsCard from './UnsubscribedEmailsCard';
import Colors from '../../constants/Colors';

// eslint-disable-next-line no-useless-escape

const DEFAULT_MAX_HOURS = 10;
const DEFAULT_FREQUENCY = 1;

const emailRegex = new RegExp(/^[^@\s]+@[^@\s\.]+(?:\.[^@\.\s]+)+$/);

const tokenSeparators = [',', '\n'];

const styles = {
  divider: {
    margin: 0,
    backgroundColor: Colors.ONTRACCR_OPACITY_GRAY,
    maxWidth: 440,
    minWidth: 440,
  },
  emailBodyInput: {
    width: 400,
  },
};

const emailBodyDebouncers = {
  equipmentHoursThresholdBody: new Debouncer(),
  equipmentMileageThresholdBody: new Debouncer(),
  equipmentDailyReportBody: new Debouncer(),
};

const generalNotificationOptions = [
  {
    key: 'shiftAssignmentUpdateNotification',
    title: 'Shift Assignment/Update Notifications',
    helpText: 'With this setting enabled, users will be sent a notification regarding shift assignments/updates',
    divider: true,
    useSwitch: true,
  },
  {
    key: 'scheduledShiftNotifications',
    title: 'Scheduled Shift Notifications',
    helpText: 'With this setting enabled, users will be sent reminders prior to their shifts',
    divider: true,
    useSwitch: true,
  },
];

const equipmentHoursThresholdWarningRow = {
  key: 'equipmentHoursThresholdWarning',
  title: 'Equipment Hours Threshold Warning (email only)',
  helpText: 'With this setting enabled, users will be sent a notification when equipment hours are at the hours threshold',
  divider: false,
  useSwitch: true,
};

const equipmentMileageThresholdWarningRow = {
  key: 'equipmentMileageThresholdWarning',
  title: 'Equipment Mileage Threshold Warning (email only)',
  helpText: 'With this setting enabled, users will be sent a notification when equipment mileage is at the mileage threshold',
  divider: false,
  useSwitch: true,
};

const equipmentDailyReportRow = {
  key: 'equipmentDailyReport',
  title: 'Daily Equipment Report (email only)',
  helpText: 'With this setting enabled, an automated daily email report will summarize equipment near or above thresholds',
  divider: false,
  useSwitch: true,
};

const notificationRecipientGroupSettings = {
  materialQuantity: {
    key: 'materialQuantityNotifications',
    title: 'Material Quantity Notifications (email only)',
    helpText: 'With this setting enabled, users will be sent an email when any total material quantity is below a preset threshold or when quantity available at any location goes below 0',
  },
  userCertification: {
    key: 'userCertificationExpirationNotification',
    title: 'User Certification Expiration Notification',
    helpText: 'With this setting enabled, users will be sent a notification when their certifications are expiring',
  },
  vendorCertification: {
    key: 'vendorCertificationExpirationNotification',
    title: 'Vendor Certification Expiration Notification',
    helpText: 'With this setting enabled, user will be sent a notification when vendor certifications are expiring',
  },
  equipmentCertification: {
    key: 'equipmentCertificationExpirationNotification',
    title: 'Equipment Certification Expiration Notification',
    helpText: 'With this setting enabled, users will be sent a notification when equipment certifications are expiring',
  },
};

const clockoutRows = [
  {
    key: 'clockoutReminderNotification',
    title: 'Clockout Reminder Notifications',
    helpText: 'With this setting enabled, users will be sent a reminder to clock out at the end of their task',
    divider: false,
    useSwitch: true,
  },
];

const frequencyRow = [{
  key: 'clockoutReminderRepeatNotification',
  title: 'Repeat Clockout Reminder Notifications',
  helpText: 'With this setting enabled, users will be sent a reminder to clock out at the end of their task every X hours',
  divider: false,
  useSwitch: true,
}];

const preferenceRows = [
  {
    key: 'emailNotification',
    title: 'Email Notification',
    helpText: 'With this setting enabled, users will be sent any notifications via email',
    divider: false,
    useSwitch: true,
  },
  {
    key: 'pushNotification',
    title: 'Push Notification',
    helpText: 'With this setting enabled, users will be sent any notifications via mobile alert',
    divider: false,
    useSwitch: true,
  },
];

const frequencyOptions = [
  { value: 1, label: '1 Hour' },
  { value: 2, label: '2 Hours' },
  { value: 3, label: '3 Hours' },
];

function NotificationPreferences({
  saveCompanySettings,
  settings = {},
}) {
  const maxDebouncer = new Debouncer();
  const {
    materialRecipients,
    materialQuantityNotifications,
    equipmentHoursThresholdWarning,
    equipmentMileageThresholdWarning,
    equipmentRecipients,
    equipmentDailyReport,
    equipmentDailyReportRecipients,
    userCertificationExpirationNotification,
    vendorCertificationExpirationNotification,
    equipmentCertificationExpirationNotification,
    userCertificationExpirationRecipients,
    vendorCertificationExpirationRecipients,
    equipmentCertificationExpirationRecipients,
  } = settings;

  const hasEquipmentThresholdWarnings = equipmentHoursThresholdWarning || equipmentMileageThresholdWarning;

  const [selectedMaterialRecipients, setSelectedMaterialRecipients] = useState(materialRecipients || []);
  const [selectedEquipmentRecipients, setSelectedEquipmentRecipients] = useState(equipmentRecipients || []);
  const [selectedEquipmentDailyReportRecipients, setSelectedEquipmentDailyReportRecipients] = useState(equipmentDailyReportRecipients || []);
  const [selectedUserCertificationExpirationRecipients, setSelectedUserCertificationExpirationRecipients] = useState(userCertificationExpirationRecipients || []);
  const [selectedVendorCertificationExpirationRecipients, setSelectedVendorCertificationExpirationRecipients] = useState(vendorCertificationExpirationRecipients || []);
  const [selectedEquipmentCertificationExpirationRecipients, setSelectedEquipmentCertificationExpirationRecipients] = useState(equipmentCertificationExpirationRecipients || []);
  const [maxHours, setMaxHours] = useState(settings.clockoutReminderMaxHour || DEFAULT_MAX_HOURS);
  const [emailBodies, setEmailBodies] = useState({
    equipmentHoursThresholdBody: settings.equipmentHoursThresholdBody || '',
    equipmentMileageThresholdBody: settings.equipmentMileageThresholdBody || '',
    equipmentDailyReportBody: settings.equipmentDailyReportBody || '',
  });

  const users = useSelector((state) => state.users.users);
  const positionNames = useSelector((state) => state.settings.positionNames);

  const userIds = useMemo(() => {
    const idSet = new Set();
    users.forEach((user) => {
      if (user.active) {
        idSet.add(user.id);
      }
    });
    return idSet;
  }, [users]);

  const positionIds = useMemo(() => {
    const idSet = new Set();
    positionNames.forEach((position) => {
      idSet.add(position.id);
    });
    return idSet;
  }, [positionNames]);

  const validate = (selectedValues) => selectedValues.filter((emailOrId) => (
    userIds.has(emailOrId)
    || positionIds.has(emailOrId)
    || emailRegex.test(emailOrId)
    || emailOrId === 'assignedUser'
  ));

  const onSettingToggle = useCallback(({ key }) => {
    saveCompanySettings({ [key]: !settings[key] });
  }, [saveCompanySettings, settings]);

  const onSelectChange = useCallback((key) => (newSelected) => {
    saveCompanySettings({ [key]: newSelected });
  }, [saveCompanySettings]);

  const onEmailBodyChange = useCallback((fieldName) => (e) => {
    const { value } = e.target;
    setEmailBodies((prev) => ({ ...prev, [fieldName]: value }));
    emailBodyDebouncers[fieldName].debounce(() => {
      saveCompanySettings({ [fieldName]: value });
    }, 500);
  }, [saveCompanySettings]);

  const onChange = useCallback((field) => (newSelected) => {
    let filteredSelected = newSelected;
    if (validate) {
      filteredSelected = validate(newSelected);
    }

    saveCompanySettings({ [field]: filteredSelected });

    switch (field) {
      case 'materialRecipients':
        setSelectedMaterialRecipients(filteredSelected);
        break;
      case 'equipmentRecipients':
        setSelectedEquipmentRecipients(filteredSelected);
        break;
      case 'equipmentDailyReportRecipients':
        setSelectedEquipmentDailyReportRecipients(filteredSelected);
        break;
      case 'userCertificationExpirationRecipients':
        setSelectedUserCertificationExpirationRecipients(filteredSelected);
        break;
      case 'vendorCertificationExpirationRecipients':
        setSelectedVendorCertificationExpirationRecipients(filteredSelected);
        break;
      case 'equipmentCertificationExpirationRecipients':
        setSelectedEquipmentCertificationExpirationRecipients(filteredSelected);
        break;
      default:
        break;
    }
  }, [validate, saveCompanySettings]);

  useEffect(() => {
    maxDebouncer.debounce(() => {
      saveCompanySettings({ clockoutReminderMaxHour: maxHours });
    }, 500);

    return () => {
      maxDebouncer.clear();
    };
  }, [maxHours]);

  return (
    <>
      <Row style={{ width: '100%', marginLeft: 180, marginTop: 30 }}>
        <CompanySettingsCard title="Notifications">
          <CheckRows
            data={generalNotificationOptions}
            isChecked={({ key }) => settings[key]}
            onChange={onSettingToggle}
          />
          <NotificationRecipientGroup
            setting={notificationRecipientGroupSettings.materialQuantity}
            isEnabled={materialQuantityNotifications}
            onToggle={onSettingToggle}
            recipients={selectedMaterialRecipients}
            onRecipientsChange={onChange('materialRecipients')}
            recipientLabel="Select Material Recipient(s):"
          />
          <NotificationRecipientGroup
            setting={notificationRecipientGroupSettings.userCertification}
            isEnabled={userCertificationExpirationNotification}
            onToggle={onSettingToggle}
            recipients={selectedUserCertificationExpirationRecipients}
            onRecipientsChange={onChange('userCertificationExpirationRecipients')}
            recipientLabel="Select User Certification Expiration Recipient(s):"
            additionalOptions={[{
              id: 'assignedUser',
              children: <AdditionalItem title="Assigned User" help="The user assigned to the certification will be sent a notification when their certifications are expiring" />,
              label: <AdditionalLabel title="Assigned User" help="The user assigned to the certification will be sent a notification when their certifications are expiring" />,
            }]}
          />
          <NotificationRecipientGroup
            setting={notificationRecipientGroupSettings.vendorCertification}
            isEnabled={vendorCertificationExpirationNotification}
            onToggle={onSettingToggle}
            recipients={selectedVendorCertificationExpirationRecipients}
            onRecipientsChange={onChange('vendorCertificationExpirationRecipients')}
            recipientLabel="Select Vendor Certification Expiration Recipient(s):"
          />
          <NotificationRecipientGroup
            setting={notificationRecipientGroupSettings.equipmentCertification}
            isEnabled={equipmentCertificationExpirationNotification}
            onToggle={onSettingToggle}
            recipients={selectedEquipmentCertificationExpirationRecipients}
            onRecipientsChange={onChange('equipmentCertificationExpirationRecipients')}
            recipientLabel="Select Equipment Certification Expiration Recipient(s):"
            showDivider={false}
          />
        </CompanySettingsCard>
      </Row>
      <Row style={{ width: '100%', marginLeft: 180 }}>
        <CompanySettingsCard title="Equipment Notification Emails">
          <CheckRows
            data={[equipmentHoursThresholdWarningRow]}
            isChecked={({ key }) => settings[key]}
            onChange={onSettingToggle}
          />
          {equipmentHoursThresholdWarning && (
            <CompanyEditRow
              title="Equipment Hours Threshold Email Body"
              helpText="Configure the email body for equipment hours threshold warning emails"
              divider={false}
            >
              <OnTraccrTextInput
                textarea
                value={emailBodies.equipmentHoursThresholdBody}
                onChange={onEmailBodyChange('equipmentHoursThresholdBody')}
                style={styles.emailBodyInput}
              />
            </CompanyEditRow>
          )}
          <CheckRows
            data={[equipmentMileageThresholdWarningRow]}
            isChecked={({ key }) => settings[key]}
            onChange={onSettingToggle}
          />
          {equipmentMileageThresholdWarning && (
            <CompanyEditRow
              title="Equipment Mileage Threshold Email Body"
              helpText="Configure the email body for equipment mileage threshold warning emails"
              divider={false}
            >
              <OnTraccrTextInput
                textarea
                value={emailBodies.equipmentMileageThresholdBody}
                onChange={onEmailBodyChange('equipmentMileageThresholdBody')}
                style={styles.emailBodyInput}
              />
            </CompanyEditRow>
          )}
          {hasEquipmentThresholdWarnings ? (
            <>
              <UserAssignmentSelector
                onChange={onChange('equipmentRecipients')}
                selected={selectedEquipmentRecipients}
                mode="tags"
                showPositions
                type="email"
                text="Equipment Threshold Warning Email Recipients"
                tokenSeparators={tokenSeparators}
              />
              <Divider
                style={styles.divider}
              />
            </>
          ) : (
            <Divider
              style={styles.divider}
            />
          )}
          <CheckRows
            data={[equipmentDailyReportRow]}
            isChecked={({ key }) => settings[key]}
            onChange={onSettingToggle}
          />
          {equipmentDailyReport && (
            <>
              <CompanyEditRow
                title="Daily Report Email Body"
                helpText="Configure the email body displayed at the top of the daily equipment report"
                divider={false}
              >
                <OnTraccrTextInput
                  textarea
                  value={emailBodies.equipmentDailyReportBody}
                  onChange={onEmailBodyChange('equipmentDailyReportBody')}
                  style={styles.emailBodyInput}
                />
              </CompanyEditRow>
              <UserAssignmentSelector
                onChange={onChange('equipmentDailyReportRecipients')}
                selected={selectedEquipmentDailyReportRecipients}
                mode="tags"
                showPositions
                type="email"
                text="Equipment Daily Report Recipients"
                tokenSeparators={tokenSeparators}
              />
              <CompanyEditRow
                title="Report Time"
                helpText="Configure the time at which the daily report email is sent"
                divider={false}
              >
                <OnTraccrTimePicker
                  date={DateTime.local().toISO()}
                  format="hh:mm a"
                  value={settings.equipmentDailyReportTime}
                  onChange={(val) => saveCompanySettings({ equipmentDailyReportTime: val })}
                  minuteIncrement={15}
                  isHourBasedObject
                />
              </CompanyEditRow>
              <CompanyEditRow
                title="Exclude Weekends"
                helpText="When enabled, the daily report will not be sent on weekends"
                divider={false}
              >
                <OnTraccrSwitch
                  value={!!settings.equipmentDailyReportExcludeWeekends}
                  onChange={() => saveCompanySettings({
                    equipmentDailyReportExcludeWeekends: !settings.equipmentDailyReportExcludeWeekends,
                  })}
                  style={{ marginRight: 40 }}
                />
              </CompanyEditRow>
            </>
          )}
        </CompanySettingsCard>
      </Row>
      <Row style={{ width: '100%', marginLeft: 180 }}>
        <CompanySettingsCard title="Task Clockout Reminders">
          <CheckRows
            data={clockoutRows}
            isChecked={({ key }) => settings[key]}
            onChange={onSettingToggle}
          />

          <CompanyEditRow
            title="Threshold"
            helpText="This field specifies the maximum total amount of hours that a user can be clocked in for the day before they are reminded to clock out."
            childColumnStyle={{ marginRight: 50 }}
            divider={false}
          >
            <InputNumber
              value={maxHours}
              onChange={(val) => setMaxHours(val ?? DEFAULT_MAX_HOURS)}
              style={{ width: 75 }}
              min={0}
              max={24}
              disabled={!settings.clockoutReminderNotification}
            />
          </CompanyEditRow>
          <CheckRows
            data={frequencyRow}
            isChecked={({ key }) => settings[key]}
            onChange={onSettingToggle}
            checkBoxDisabled={() => !settings.clockoutReminderNotification}
          />

          <CompanyEditRow
            title="Frequency"
            helpText="This field specifies the frequency in hours at which users will be reminded to clock out."
            childColumnStyle={{ marginRight: 50 }}
            divider={false}
          >
            <Select
              options={frequencyOptions}
              value={settings.clockoutReminderFrequency}
              onChange={onSelectChange('clockoutReminderFrequency')}
              defaultValue={DEFAULT_FREQUENCY}
              disabled={!settings.clockoutReminderRepeatNotification
                || !settings.clockoutReminderNotification}
            />
          </CompanyEditRow>
        </CompanySettingsCard>
      </Row>
      <UnsubscribedEmailsCard />
      <Row style={{ width: '100%', marginLeft: 180 }}>
        <CompanySettingsCard title="Preferences" style={{ marginBottom: 50 }}>
          <CheckRows
            data={preferenceRows}
            isChecked={({ key }) => settings[key]}
            onChange={onSettingToggle}
          />
        </CompanySettingsCard>
      </Row>
    </>
  );
}

NotificationPreferences.propTypes = {
  saveCompanySettings: PropTypes.func.isRequired,
  settings: PropTypes.shape({
    emailNotification: PropTypes.bool,
    pushNotification: PropTypes.bool,
  }),
};

NotificationPreferences.defaultProps = {
  settings: {},
};

export default NotificationPreferences;
